import React, { useEffect, useState, useCallback } from "react";
import Cookies from "js-cookie";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Samme mønster som butikkvalget i NavBar.js: to cookies (navn + id) satt sammen, lest direkte
// med Cookies.get(...) der de trengs — ingen delt Context. Klubb og gren gjenopprettes fra
// cookie ved neste besøk; er cookien ugyldig (klubben/grenen finnes ikke lenger, eller er tom)
// velges første i lista automatisk, slik at eksisterende ettklubb/ettgren-bruk ikke endrer seg.
export function getKlubbId() {
  return Cookies.get("klubbId") || "";
}
export function getGrenId() {
  return Cookies.get("grenId") || "";
}
export function getKlubbNavn() {
  return Cookies.get("selectedKlubb") || "";
}

export default function KlubbVelger({ onChange }) {
  const [clubs, setClubs] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [clubId, setClubId] = useState("");
  const [grenId, setGrenId] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const applySelection = useCallback(
    (nextClubId, nextClubName, nextGrenId, nextGrenName) => {
      Cookies.set("klubbId", nextClubId || "");
      Cookies.set("selectedKlubb", nextClubName || "");
      Cookies.set("grenId", nextGrenId || "");
      Cookies.set("selectedGren", nextGrenName || "");
      setClubId(nextClubId || "");
      setGrenId(nextGrenId || "");
      window.dispatchEvent(new Event("klubbUpdated"));
      onChange?.(nextClubId || "", nextGrenId || "");
    },
    [onChange]
  );

  const loadDepartmentsFor = useCallback(
    async (selectedClubId, preferredGrenId) => {
      const res = await fetch(`${API_BASE_URL}/klubbsystem/departments?clubId=${selectedClubId}`);
      const data = await res.json();
      setDepartments(data);
      const club = clubs.find((c) => c._id === selectedClubId);
      const stillValid = data.some((d) => d._id === preferredGrenId);
      const nextGren = stillValid ? data.find((d) => d._id === preferredGrenId) : data[0];
      applySelection(selectedClubId, club?.name, nextGren?._id || "", nextGren?.name || "");
    },
    [clubs, applySelection]
  );

  useEffect(() => {
    async function loadClubs() {
      try {
        const res = await fetch(`${API_BASE_URL}/klubbsystem/clubs`);
        const data = await res.json();
        setClubs(data);

        const cookieClubId = getKlubbId();
        const stillValid = data.some((c) => c._id === cookieClubId);
        const effectiveClub = stillValid ? data.find((c) => c._id === cookieClubId) : data[0];
        if (effectiveClub) {
          const depsRes = await fetch(`${API_BASE_URL}/klubbsystem/departments?clubId=${effectiveClub._id}`);
          const deps = await depsRes.json();
          setDepartments(deps);
          const cookieGrenId = getGrenId();
          const grenStillValid = deps.some((d) => d._id === cookieGrenId);
          const effectiveGren = grenStillValid ? deps.find((d) => d._id === cookieGrenId) : deps[0];
          applySelection(effectiveClub._id, effectiveClub.name, effectiveGren?._id || "", effectiveGren?.name || "");
        }
      } finally {
        setLoading(false);
      }
    }
    loadClubs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClubChange = (e) => {
    const nextClubId = e.target.value;
    loadDepartmentsFor(nextClubId, null);
  };

  const handleGrenChange = (e) => {
    const nextGrenId = e.target.value;
    const club = clubs.find((c) => c._id === clubId);
    const gren = departments.find((d) => d._id === nextGrenId);
    applySelection(clubId, club?.name, gren?._id || "", gren?.name || "");
  };

  const handleOutsideClick = (event) => {
    if (event.target.classList.contains("modal-background")) setIsOpen(false);
  };

  if (loading) return null;

  // Skjules helt for dagens ettklubb/ettgren-bruk — ingenting å velge mellom ennå.
  if (clubs.length <= 1 && departments.length <= 1) return null;

  const club = clubs.find((c) => c._id === clubId);
  const gren = departments.find((d) => d._id === grenId);

  return (
    <div className="mb-6 flex items-center gap-2 text-sm">
      <span className="text-gray-500">Klubb:</span>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="font-semibold text-gray-900 hover:text-indigo-600 underline decoration-dotted underline-offset-4"
      >
        {club?.name || "Velg klubb"}
        {gren?.name ? ` · ${gren.name}` : ""}
      </button>

      {isOpen && (
        <div
          className="modal-background fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={handleOutsideClick}
        >
          <div className="bg-white p-6 rounded-2xl shadow-2xl w-80">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Velg klubb og idrett</h2>

            <label className="block text-xs font-medium text-gray-500 mb-1">Klubb</label>
            <select
              value={clubId}
              onChange={handleClubChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {clubs.map((c) => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>

            <label className="block text-xs font-medium text-gray-500 mb-1">Idrett (gren)</label>
            <select
              value={grenId}
              onChange={handleGrenChange}
              disabled={departments.length === 0}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {departments.length === 0 && <option value="">Ingen grener i denne klubben</option>}
              {departments.map((d) => (
                <option key={d._id} value={d._id}>{d.name}</option>
              ))}
            </select>

            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="mt-5 w-full px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold"
            >
              Ferdig
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
